import {
	AspectMaintainer, Button, ButtonTypes, Card, type SelectOption, Select, SettingsLayout, Switch,
	InteractiveSvg
} from "@clintonelec/react-storybook";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { RoiQuality } from "Data/Objects/Camera";
import { selectRoiSettings, setRoiSettingsAction } from "Data/Redux/Slices/Settings/Camera/Roi";
import { useAppSelector } from "Data/Redux/Store";
import { produce } from "immer";
import { constant, isEqual, times } from "lodash";
import { type FormEvent, memo, useState } from "react";
import { useDispatch } from "react-redux";
import "Components/ConfigLayout/Camera/RoiSettings/RoiSettings.less";
import type { Point } from "@clintonelec/react-storybook";

const qualityOptions: SelectOption[] = [
	{
		label: "Low",
		value: RoiQuality.LOW
	},
	{
		label: "Medium",
		value: RoiQuality.MEDIUM
	},
	{
		label: "High",
		value: RoiQuality.HIGH
	}
];

const defaultZonePoints = [
	{ x: 760, y: 340 },
	{ x: 1160, y: 340 },
	{ x: 1160, y: 740 },
	{ x: 760, y: 740 }
];

function RoiSettings() {
	const dispatch = useDispatch();
	const roiSettings = useAppSelector(selectRoiSettings);
	const [ localRoiSettings, setLocalRoiSettings ] = useState(roiSettings);
	const [ selectedZone, setSelectedZone ] = useState<number | null>(null);
	const setRoiSettings = () => dispatch(setRoiSettingsAction(localRoiSettings));
	const allZonesEmpty = !localRoiSettings.zones.some(zone => !!zone);

	const handleRoiQualitySelect = (value: string | null) => {
		if (!value) {
			return;
		}

		const newLocalState = produce(localRoiSettings, draft => {
			draft.quality = value;
		});

		setLocalRoiSettings(newLocalState);
	};

	const handleZoneButtonClick = (zoneIndex: number) => () => {
		const newIndex = selectedZone === zoneIndex ? null : zoneIndex;

		setSelectedZone(newIndex);

		if(newIndex !== null && localRoiSettings.zones[ newIndex ]?.length === 0) {
			const newLocalState = produce(localRoiSettings, draft => {
				draft.zones[ newIndex ] = defaultZonePoints;
			});

			setLocalRoiSettings(newLocalState);
		}
	};

	const handlePointsUpdate = (newPoints: Point[]) => {
		if (selectedZone === null) {
			return;
		}

		const newLocalState = produce(localRoiSettings, draft => {
			draft.zones[ selectedZone ] = newPoints;
		});

		setLocalRoiSettings(newLocalState);
	};

	const handleClearActiveZone = () => {
		if (selectedZone === null) {
			return;
		}

		const newLocalState = produce(localRoiSettings, draft => {
			draft.zones[ selectedZone ] = null;
		});

		setLocalRoiSettings(newLocalState);
		setSelectedZone(null);
	};

	const handleClearAllZones = () => {
		const newLocalState = produce(localRoiSettings, draft => {
			draft.zones = times(8, (constant(null)));
		});

		setLocalRoiSettings(newLocalState);
		setSelectedZone(null);
	};

	const handleFormReset = (event: FormEvent) => {
		event.preventDefault();

		setLocalRoiSettings(roiSettings);
		setSelectedZone(null);
	};

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault();

		setRoiSettings();
		setSelectedZone(null);
	};

	const handlePrivacyRoiEnabledChange = (newEnabled: boolean) => {
		const newLocalState = produce(localRoiSettings, draft => {
			draft.roiEnabled = newEnabled;
		});

		setLocalRoiSettings(newLocalState);
		setSelectedZone(null);
	};

	const handleCreateZoneClick = (index: number) => () => {
		setSelectedZone(index);
		setLocalRoiSettings(produce(localRoiSettings, (draft) => {
			draft.zones[ index ] = defaultZonePoints;
		}));
	};

	const renderZoneButtons = () => times(8, zoneIndex => {
		const selectedButton = selectedZone === zoneIndex;

		if (!localRoiSettings?.zones[ zoneIndex ]) {
			return (
				<Button
					key={ zoneIndex }
					className="zone-button"
					disabled={ !localRoiSettings.roiEnabled }
					ghost={ !selectedButton }
					htmlType="button"
					onClick={ handleCreateZoneClick(zoneIndex) }
					type={ ButtonTypes.SECONDARY }
				>
					+
				</Button>
			);
		}

		return (
			<Button
				key={ zoneIndex }
				className="zone-button"
				disabled={ !localRoiSettings.roiEnabled }
				ghost={ !selectedButton }
				htmlType="button"
				onClick={ handleZoneButtonClick(zoneIndex) }
				type={ !selectedButton ? ButtonTypes.SECONDARY : ButtonTypes.TERTIARY }
			>
				{ `Zone ${ zoneIndex + 1 }` }
			</Button>
		);
	});

	const renderInteractivePolygon = () => {
		if (selectedZone === null) {
			return;
		}

		return (
			<InteractiveSvg>
				<InteractiveSvg.Polygon
					key={ `zone-${ selectedZone }` }
					points={ localRoiSettings.zones[ selectedZone ] ?? [] }
					onChange={ handlePointsUpdate } />
			</InteractiveSvg>
		);
	};

	return (
		<SettingsLayout.Form
			className="roi-settings"
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title="Region of Interest">
					<div className="two-column-card-content">
						<div className="left-content">
							<div className="form-row">
								<Switch
									checked={ localRoiSettings.roiEnabled }
									className="roi-switch"
									onChange={ handlePrivacyRoiEnabledChange }
								/>
								<span>Enable ROI Mode</span>
							</div>
							<div className="camera-container">
								<AspectMaintainer>
									<div className="camera-snapshot">
										{ renderInteractivePolygon() }
									</div>
								</AspectMaintainer>
							</div>
							<div className="controls-container">
								<div className="zone-buttons">
									{ renderZoneButtons() }
								</div>
								<Button
									className="zone-action-button"
									disabled={ !localRoiSettings.roiEnabled || selectedZone === null }
									ghost
									htmlType="button"
									onClick={ handleClearActiveZone }
									type={ ButtonTypes.SECONDARY }
								>
									Clear Active Zone
								</Button>
								<Button
									className="zone-action-button"
									disabled={ !localRoiSettings.roiEnabled || allZonesEmpty }
									type={ ButtonTypes.SECONDARY }
									ghost
									htmlType="button"
									onClick={ handleClearAllZones }
								>
									Clear All Zones
								</Button>
							</div>
						</div>
						<div className="right-content">
							<div className="form-row roi-quality">
								<span>ROI Quality</span>
								<Select
									allowClear={ false }
									className="form-select"
									onSelect={ handleRoiQualitySelect }
									options={ qualityOptions }
									value={ localRoiSettings.quality }
								/>
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(roiSettings, localRoiSettings) } />
		</SettingsLayout.Form>
	);
}

export default memo(RoiSettings);
